import React from 'react';
import { Select as MuiSelect, MenuItem, styled } from '@mui/material';
import { ExpandMore, Check } from '@mui/icons-material';
import { Colors } from 'library';

type SelectTone = 'primary' | 'muted' | 'error';

interface SelectOption {
    key: string | number;
    value: string;
    label?: React.ReactNode;
}

interface SelectProps {
    value: string | number;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    tone?: SelectTone;
    active?: boolean;
}

const TONE_BG: Record<SelectTone, string> = {
    primary: Colors.white,
    muted: Colors.surface,
    error: Colors.errorBackground,
};

const TONE_BORDER: Record<SelectTone, string> = {
    primary: Colors.border,
    muted: Colors.border,
    error: Colors.error,
};

const TONE_FOCUS_BORDER: Record<SelectTone, string> = {
    primary: Colors.textSecondary,
    muted: Colors.textSecondary,
    error: Colors.error,
};

export function Select({
    value,
    onChange,
    options,
    placeholder,
    disabled,
    fullWidth,
    tone = 'muted',
    active = false,
}: SelectProps) {
    return (
        <StyledSelect
            value={value}
            onChange={(e) => onChange(e.target.value as string)}
            disabled={disabled}
            fullWidth={fullWidth}
            displayEmpty={!!placeholder}
            IconComponent={StyledExpandMore}
            $tone={tone}
            $active={active}
            MenuProps={{
                PaperProps: {
                    sx: {
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        marginTop: '4px',
                    },
                },
            }}
            renderValue={(selected) => {
                if (placeholder && !selected) return <Placeholder>{placeholder}</Placeholder>;
                const selectedOption = options.find((o) => String(o.key) === String(selected));
                return selectedOption ? selectedOption.label ?? selectedOption.value : '';
            }}
        >
            {options.map((opt) => (
                <StyledMenuItem key={opt.key} value={opt.key} selected={String(opt.key) === String(value)}>
                    {String(opt.key) === String(value) && <Check sx={{ fontSize: 18, marginRight: '8px' }} />}
                    {opt.label ?? opt.value}
                </StyledMenuItem>
            ))}
        </StyledSelect>
    );
}

const StyledExpandMore = styled(ExpandMore)({
    fontSize: 20,
    color: Colors.textSecondary,
});

const StyledSelect = styled(MuiSelect, { shouldForwardProp: (p) => p !== '$tone' && p !== '$active' })<{
    $tone: SelectTone;
    $active: boolean;
}>(({ $tone, $active }) => ({
    fontSize: 14,
    color: $active ? Colors.secondary : Colors.primary,
    backgroundColor: $active ? Colors.secondarySoft : TONE_BG[$tone],
    borderRadius: 10,
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: $active ? Colors.secondary : TONE_BORDER[$tone],
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: $active ? Colors.secondary : TONE_BORDER[$tone],
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: $active ? Colors.secondary : TONE_FOCUS_BORDER[$tone],
        borderWidth: 1,
    },
    '&.Mui-disabled': {
        backgroundColor: Colors.fill,
        color: Colors.textSecondary,
    },
    '& .MuiSelect-select': {
        padding: '10px 14px',
    },
    '& .MuiSelect-icon': {
        color: $active ? Colors.secondary : Colors.textSecondary,
    },
}));

const StyledMenuItem = styled(MenuItem)({
    fontSize: 14,
    padding: '7px 14px',
    margin: '0 8px',
    minHeight: 'unset',
    borderRadius: 8,
    '&.Mui-selected': {
        backgroundColor: 'transparent',
        color: Colors.primary,
    },
    '&.Mui-selected:hover': {
        backgroundColor: Colors.secondary,
        color: Colors.white,
    },
    '&:hover': {
        backgroundColor: Colors.secondary,
        color: Colors.white,
    },
});

const Placeholder = styled('span')({
    color: Colors.border,
});
